import { isArray } from 'lodash'
import sortBy from './sortBy'
import {
  formatBlockLegacyAuthenticationReportData,
  formatBlockSignInSharedMailboxesReportData,
  formatInactiveAccountsReportData,
  formatPowershellAccessForNonAdminsReportData,
  formatSelfServePasswordResetReportData,
} from './postureCheckExportFormatters'
import {
  employeeNameSort,
  formatConditionalAccessPolicyCount,
  formatMfaStatusType,
  getAuthenticationTypeFormatter,
} from './table.util'

export const getMfaConfiguration = (item, intl) => {
  const configurationStatus = item?.mfaConfigurations?.configurationStatus
  const numberCaPolicies = item?.mfaConfigurations?.affectedByCAs?.length

  const configurationStatusMessage = configurationStatus
    ? intl.formatMessage({ id: `INTEGRATION.${configurationStatus}` })
    : ''
  const isAffectedByCAs = ['CAP', 'DUO', 'DUO_AND_CAP'].includes(configurationStatus)
  const numberCaPoliciesMessage = formatConditionalAccessPolicyCount(numberCaPolicies, intl)

  return `${configurationStatusMessage} ${isAffectedByCAs ? '/' : ''} ${
    isAffectedByCAs ? numberCaPoliciesMessage : ''
  }`.trim()
}

export const getMfaStatus = (item, intl) => {
  const mfaStatus = item?.mfaStatus?.status
  const mfaStatusMessage = mfaStatus ? intl.formatMessage({ id: `INTEGRATION.${mfaStatus}` }) : ''

  const statusList = item.mfaStatus?.statusList ?? []
  const statusListMessage = statusList
    .map((obj) =>
      `${formatMfaStatusType(obj.type, intl)} ${obj.status ? '-' : ''} ${
        obj.status ? intl.formatMessage({ id: `GENERAL.${obj.status}` }) : ''
      }`.trim(),
    )
    .join(', ')

  return `${mfaStatusMessage} ${statusListMessage ? '/' : ''} ${statusListMessage}`.trim()
}

const formatMfaReportData = ({ employees }, { order }, intl) =>
  employees
    .sort((a, b) => employeeNameSort(a, b, order, undefined, a, b))
    .map(({ email, licenseType, role, ...item }) => ({
      name: item.displayName || `${item.firstName} ${item.lastName}`,
      email,
      licenseType,
      role,
      mfaConfiguration: getMfaConfiguration(item, intl),
      mfaStatus: getMfaStatus(item, intl),
      authenticationType: item.authenticationType?.map(getAuthenticationTypeFormatter(intl)).join(', '),
    }))

const formatLicenseReportData = ({ users }, { order }) => {
  const sortedUsers = order ? sortBy(users, { field: 'name', order }) : users

  return sortedUsers.map(({ email, roles, licenses, change, status, name }) => ({
    employee: name,
    email,
    role: roles.join(','),
    licenses: licenses.join(','),
    change: change.join(','),
    status,
  }))
}

const formatComplianceAuditReportData = (data, sort) => {
  const postureChecks = isArray(data) ? data : data.postureChecks

  return sortBy(postureChecks, sort).map(
    ({ formattedName, formattedStatus, category, formattedTemplates, msSecureScore }) => ({
      formattedName,
      formattedStatus,
      category,
      formattedTemplates: formattedTemplates.join(','),
      msSecureScoreCurrentScore: msSecureScore && parseFloat(msSecureScore.currentScore),
      msSecureScoreMaxScore: msSecureScore && parseFloat(msSecureScore.maxScore),
    }),
  )
}

const formatPreformattedExportData = (data) => data

export default {
  MFA_REPORT: formatMfaReportData,
  LICENSE_REPORT: formatLicenseReportData,
  COMPLIANCE_AUDIT_REPORT: formatComplianceAuditReportData,
  BLOCK_LEGACY_AUTHENTICATION_REPORT: formatBlockLegacyAuthenticationReportData,
  BLOCK_SIGN_IN_SHARED_MAILBOXES_REPORT: formatBlockSignInSharedMailboxesReportData,
  INACTIVE_ACCOUNTS_REPORT: formatInactiveAccountsReportData,
  POWERSHELL_ACCESS_FOR_NON_ADMINS_REPORT: formatPowershellAccessForNonAdminsReportData,
  SELF_SERVE_PASSWORD_RESET_REPORT: formatSelfServePasswordResetReportData,
  COMPLIANCE_CHECK_ENTITY_REPORT: formatPreformattedExportData,
}
